import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api, extractApiError } from '../api/client';
import { SkeletonCard } from '../components/SkeletonLoader';
import type { BetaUser } from '@shared/types';

const STATUS_COLORS: Record<string, string> = {
  invited: 'bg-yellow-900/50 text-yellow-400',
  onboarded: 'bg-blue-900/50 text-blue-400',
  active: 'bg-green-900/50 text-green-400',
  churned: 'bg-gray-800 text-gray-500',
};

export default function BetaPage() {
  const { id: startupId } = useParams<{ id: string }>();
  const [betaUsers, setBetaUsers] = useState<BetaUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', profession: '' });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [inviteLink, setInviteLink] = useState<Record<string, string>>({});

  const fetchBetaUsers = async () => {
    if (!startupId) return;
    setIsLoading(true);
    try {
      const { data } = await api.get(`/beta/startup/${startupId}`);
      setBetaUsers(data.data.betaUsers);
    } catch (err) {
      console.error(extractApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchBetaUsers(); }, [startupId]);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email';
    if (!form.profession.trim()) errs.profession = 'Profession is required';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      const { data } = await api.post('/beta', { startupId, ...form });
      setBetaUsers((prev) => [...prev, data.data.betaUser]);
      setForm({ name: '', email: '', profession: '' });
      setShowForm(false);
    } catch (err) {
      const apiErr = extractApiError(err);
      if (apiErr.fields) setFormErrors(apiErr.fields);
      else alert(apiErr.message);
    } finally {
      setIsSaving(false);
    }
  };

  const generateInvite = async (userId: string) => {
    try {
      const { data } = await api.post(`/beta/${userId}/invite`);
      setInviteLink((prev) => ({ ...prev, [userId]: data.data.inviteUrl }));
    } catch (err) {
      alert(extractApiError(err).message);
    }
  };

  const copyLink = (userId: string) => {
    const link = inviteLink[userId];
    if (!link) return;
    navigator.clipboard.writeText(link);
    setCopiedId(userId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const updateStatus = async (userId: string, status: string) => {
    await api.put(`/beta/${userId}`, { status });
    setBetaUsers((prev) => prev.map((u) => u.id === userId ? { ...u, status: status as BetaUser['status'] } : u));
  };

  const removeUser = async (userId: string) => {
    if (!confirm('Remove this beta user?')) return;
    await api.delete(`/beta/${userId}`);
    setBetaUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const count = betaUsers.length;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Beta Testing</h1>
          <p className="text-gray-400 mt-1">Manage your beta users and generate feedback links</p>
        </div>
        {count < 10 && (
          <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
            + Add Beta User
          </button>
        )}
      </div>

      {/* Warning at 5 users */}
      {count >= 5 && count < 10 && (
        <div className="mb-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg px-4 py-2 text-yellow-400 text-sm">
          💡 You have {count} beta users. 5–10 users is ideal for early validation before scaling feedback collection.
        </div>
      )}
      {count >= 10 && (
        <div className="mb-4 bg-blue-900/20 border border-blue-700/50 rounded-lg px-4 py-2 text-blue-400 text-sm">
          Maximum of 10 beta users reached. Remove a user to add more.
        </div>
      )}

      {/* Add user form */}
      {showForm && (
        <div className="card mb-6 animate-slide-up">
          <h2 className="text-lg font-bold text-white mb-4">Add Beta User</h2>
          <form onSubmit={handleAdd} className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Smith"
                className={`input-field ${formErrors.name ? 'error' : ''}`}
              />
              {formErrors.name && <p className="error-text">{formErrors.name}</p>}
            </div>
            <div>
              <label className="label">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jane@example.com"
                className={`input-field ${formErrors.email ? 'error' : ''}`}
              />
              {formErrors.email && <p className="error-text">{formErrors.email}</p>}
            </div>
            <div>
              <label className="label">Professional Background *</label>
              <input
                type="text"
                value={form.profession}
                onChange={(e) => setForm({ ...form, profession: e.target.value })}
                placeholder="e.g. Freelance Designer"
                className={`input-field ${formErrors.profession ? 'error' : ''}`}
              />
              {formErrors.profession && <p className="error-text">{formErrors.profession}</p>}
            </div>
            <div className="sm:col-span-3 flex gap-3">
              <button type="submit" disabled={isSaving} className="btn-primary">
                {isSaving ? 'Adding...' : 'Add User'}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Beta users list */}
      {isLoading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[1, 2, 3].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : betaUsers.length === 0 ? (
        <div className="card text-center py-16">
          <div className="text-4xl mb-3">🧪</div>
          <h2 className="text-xl font-bold text-white mb-2">No beta users yet</h2>
          <p className="text-gray-400 mb-4">Add your first beta user to start collecting feedback.</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">Add first beta user</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {betaUsers.map((user) => (
            <div key={user.id} className="card space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-white">{user.name}</h3>
                  <p className="text-gray-400 text-sm">{user.profession}</p>
                  <p className="text-gray-600 text-xs">{user.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`badge ${STATUS_COLORS[user.status]}`}>{user.status}</span>
                  <button onClick={() => removeUser(user.id)} className="text-gray-600 hover:text-red-400 transition-colors text-sm">✕</button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#0f0f1a] rounded-lg p-2 text-center">
                  <p className="text-xs text-gray-500">Feedback</p>
                  <p className="text-lg font-bold text-white">{user.feedbackCount}</p>
                </div>
                <div className="bg-[#0f0f1a] rounded-lg p-2 text-center">
                  <p className="text-xs text-gray-500">NPS Score</p>
                  <p className={`text-lg font-bold ${
                    user.npsScore === null ? 'text-gray-600' :
                    user.npsScore >= 9 ? 'text-green-400' :
                    user.npsScore >= 7 ? 'text-yellow-400' : 'text-red-400'
                  }`}>
                    {user.npsScore !== null ? user.npsScore : '—'}
                  </p>
                </div>
              </div>

              {/* Status selector */}
              <select
                value={user.status}
                onChange={(e) => updateStatus(user.id, e.target.value)}
                className="input-field text-sm"
              >
                <option value="invited">Invited</option>
                <option value="onboarded">Onboarded</option>
                <option value="active">Active</option>
                <option value="churned">Churned</option>
              </select>

              {/* Invite link */}
              {inviteLink[user.id] ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={inviteLink[user.id]}
                    className="input-field text-xs flex-1"
                  />
                  <button onClick={() => copyLink(user.id)} className="btn-primary text-xs px-3">
                    {copiedId === user.id ? '✓' : 'Copy'}
                  </button>
                </div>
              ) : (
                <button onClick={() => generateInvite(user.id)} className="btn-secondary text-sm w-full">
                  🔗 Generate invite link
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
